import type {
  AoaInfo,
  AoaItem,
  AoaItemType,
  AoaProgressStatus,
  AoaSection,
  IterationUnavailableReason,
  KpiComparison,
} from '@packages/types'
import type { BaseTranslation } from 'typesafe-i18n'

type ActivityEntries =
  | 'createdAoa'
  | 'createdItem'
  | 'deletedAoa'
  | 'deletedItem'
  | 'createdCheckin'
  | 'approvedAoa'
  | 'updatedAoa'
  | 'closedAoa'
  | 'updatedItem'

const en_aoa = {
  aoa: {
    activity: {
      generalActivity: {
        createdAoa: 'Created new <></>.',
        createdItem: 'Created new {itemDescription:string} <></>.',
        deletedAoa: 'Deleted <></>.',
        deletedItem: 'Deleted {itemDescription:string} <></>.',
        createdCheckin: 'Created a new check-in for <></>.',
        approvedAoa: 'Approved <></>.',
        updatedAoa: 'Edited <></>.',
        closedAoa: 'Closed <></>.',
        updatedItem: 'Edited {itemDescription:string} <></>.',
      } satisfies Record<ActivityEntries, string>,
      scopedActivity: {
        createdAoa: 'Created this AOA.',
        createdItem: 'Created this {itemType:string}.',
        deletedAoa: 'Deleted this AOA.',
        deletedItem: 'Deleted this {itemType:string}.',
        createdCheckin: 'Created a new check-in.',
        approvedAoa: 'Approved this AOA.',
        updatedAoa: 'Edited this AOA.',
        closedAoa: 'Closed this AOA.',
        updatedItem: 'Edited this {itemType:string}.',
      } satisfies Record<ActivityEntries, string>,
      updatedAttributes: 'Updated: <></>.',
      updatedAttributesEntries: {
        comment: 'Comment',
        description: 'Description',
        text: 'Title',
        section: 'Section',
        results: '__Targets__',
        responsibleUserId: 'Responsible Person',
        movedToAoaId: 'Moved to another AOA',
        periodEnd: 'End Date',
        periodStart: 'Start Date',
        itemType: 'Type',
        tags: 'Tags',
        iteration: 'Iteration',
        title: 'Title',
        status: 'Status',
      } satisfies Partial<Record<keyof AoaItem | keyof AoaInfo, string>>,
      activityTooltips: {
        allTagsRemoved: 'All tags removed',
      },
    },
    itemTypes: {
      action: '__Action__',
      intent: '__Intent__',
      outcome: '__Target__',
      result: '__Deliverable__',
      results: '__Deliverables__',
      text: 'Text',
      higherIntent: '__Higher Intent__',
    } satisfies Record<AoaItemType, string> & Record<string, string>,
    createItemTypes: {
      action: 'Create new __Action__',
      intent: 'Create new __Intent__',
      outcome: 'Create new __Target__',
      text: 'Create new entry',
      higherIntent: 'Create new __Higher Intent__',
    } satisfies Record<AoaItemType | 'higherIntent', string>,
    editItemTypes: {
      action: 'Edit __Action__',
      intent: 'Edit __Intent__',
      outcome: 'Edit __Target__',
      text: 'Edit entry',
    } satisfies Record<AoaItemType, string>,
    itemTypeTextFieldPlaceholder: {
      action: '__Action__ title...',
      intent: '__Intent__ title...',
      outcome: '__Target__ title...',
      text: 'Text...',
    } satisfies Record<AoaItemType, string>,
    itemTypeDescriptions: {
      action:
        'A concrete __Action__ with a clear expectation of achievement in line with the parent __Intent__.',
      intent:
        'A measure that concretizes the field of action defined in the parent __Intent__.',
      outcome: '',
      text: '',
    } satisfies Record<AoaItemType, string>,
    metrics: {
      vsLastIteration: 'vs last iteration',
      executionProgress: 'Execution Progress',
      status: 'Status',
      confidenceLevel: 'Confidence Level',
      cancellation: 'Cancellation',
      noDataAvailable: 'No data available',
      completion: 'Completion',
      myCompletion: 'My Completion',
      resultCompletion: 'Result Completion Rate',
      myResultCompletion: 'My Effective Result Completion Rate',
      myConfidence: 'My Confidence',
      myExecutionProgress: 'My Execution Progress',
      myCancellation: 'My Cancellation',
      completedResults: 'Completed Results',
      totalResults: 'Total Results',
      sunburstChart: {
        companyStrategy: 'Company Strategy',
        colorBy: 'Color by',
        displayChartBy: 'Display chart by',
        byAoasActions: 'Organization',
        byStrategy: 'Strategy',
        byTargets: '__Targets__',
      },
      showIntentsWithNoChildIntents: 'Show all __intents__',
    },
    targets: {
      startValue: 'Start Value',
      targetValue: '__Target__ Value',
      currentValue: 'Current Value',
    },
    dashboard: {
      actionsCard: {
        empty: {
          headline: 'No __actions__ to show',
          description:
            'There are no __actions__ matching the current filters yet.',
        },
        sort: {
          label: 'Sort by',
          confidence: 'Confidence',
          progress: 'Progress',
          status: 'Status',
          direction: {
            ascending: 'Ascending',
            descending: 'Descending',
          },
        },
      },
      activityFeed: {
        cardHeader: 'Latest Updates',
        cardDescription: {
          active:
            'Your team has made {recentCount:number} updates in the last {days:number} days.',
          noRecent:
            'Your team has not made any updates in the last {days:number} days.',
        },
        userCardDescription: {
          active:
            '{userName:string} has made {recentCount:number} updates in the last {days:number} days.',
          noRecent:
            '{userName:string} has not made any updates in the last {days:number} days.',
        },
        emptyState: {
          header: 'No recent activity',
          description:
            'There are no recent __actions__ or updates in this AOA.',
          descriptionUserActivity:
            "{userName:string} hasn't made any updates yet.",
        },
      },
      statCards: {
        finishedActions: 'Finished __Actions__',
        delayedActions: 'Delayed __Actions__',
        progressRate: 'Progress Rate',
      },
      filterBar: {
        displayModes: {
          strategy: 'Strategy',
          targets: '__Targets__',
        },
        metrics: {
          confidence: 'Confidence',
          progress: 'Progress',
          status: 'Status',
        },
        filters: {
          statusFilter: 'Status Filter',
          confidenceFilter: 'Confidence Filter',
          progressFilter: 'Progress Filter',
        },
      },
      myActions: {
        cardHeader: 'My Core Activities',
        cardDescription: {
          due: 'You were assigned {totalCount:number} __actions__, and {dueCount:number} are awaiting your check-in.',
          noDue:
            'You were assigned {totalCount:number} __actions__. All __actions__ have been checked in.',
          noActions: 'You have no __actions__ assigned to you.',
        },
        checkinBadge:
          '{checkedinCount:number} / {totalCount:number} checked-in',
        emptyState: {
          header: 'No __actions__ assigned',
          description:
            "You weren't assigned any __actions__ to check in for this aoa.",
        },
      },
      userActions: {
        cardHeader: '{userName:string}’s Core Activities',
        cardDescription: {
          due: '{userName:string} was assigned {totalCount:number} __actions__, and {dueCount:number} are awaiting check-in.',
          noDue:
            '{userName:string} was assigned {totalCount:number} __actions__. All __actions__ have been checked in.',
          noActions: '{userName:string} has no __actions__ assigned.',
        },
        checkinBadge:
          '{checkedinCount:number} / {totalCount:number} checked-in',
        emptyState: {
          header: 'No __actions__ assigned',
          description:
            "{userName:string} wasn't assigned any __actions__ to check in for this aoa.",
        },
      },
      confidenceSunburst: {
        cardHeader: 'Confidence Level Distribution',
        cardDescription:
          'Confidence level of the __targets__ and __actions__ in the AOA.',
      },
      notificableOutcomes: {
        cardHeader: 'Notificable __Targets__',
        cardDescription:
          '__Targets__ whose Confidence Level is less than 50% or are currently blocked will be shown here.',
        emptyState: {
          header: 'No notificable __targets__',
          description:
            'There are no __targets__ that require your attention at the moment.',
        },
        badge: '{itemsAtRiskCount:number} __targets__ at risk',
      },
      outcomeList: {
        empty: {
          headline: 'No Targets Found',
          description: 'There are no __targets__ available for this AOA.',
        },
        selectDepartmentPlaceholder:
          'Select a department to see its __targets__',
      },
    },
    buttons: {
      collapseItemCount: 'Show {0:number} item{{s}}',
      createNewAoa: 'Create New AOA',
      createRootAoa: 'Create Company AOA',
      newAoa: 'New AOA',
    },
    checkin: {
      commentStep: 'Do you have any comments or notes to add?',
      comperatorTexts: {
        labels: {
          equal: 'Equal to',
          greaterThan: 'Greater than',
          greaterThanOrEqual: 'Greater than or equal to',
          lessThan: 'Less than',
          lessThanOrEqual: 'Less than or equal to',
        } satisfies Record<KpiComparison, string>,
        targetShort: {
          equal: ' ',
          greaterThan: 'at least ',
          greaterThanOrEqual: 'at least ',
          lessThan: 'less than ',
          lessThanOrEqual: 'less than ',
        } satisfies Record<KpiComparison, string>,
        targetValueOnly: {
          prefix: {
            equal: 'Result equals ',
            greaterThan: 'Result is greater than ',
            greaterThanOrEqual: 'Result is greater than or equal to ',
            lessThan: 'Result is less than ',
            lessThanOrEqual: 'Result is less than or equal to ',
          } satisfies Record<KpiComparison, string>,
          suffix: {
            equal: '',
            greaterThan: '',
            greaterThanOrEqual: '',
            lessThan: '',
            lessThanOrEqual: '',
          } satisfies Record<KpiComparison, string>,
        },
        startAndTargetValue: {
          prefix: {
            equal: 'Result starts at ',
            greaterThan: 'Result starts at ',
            greaterThanOrEqual: 'Result starts at ',
            lessThan: 'Result starts at ',
            lessThanOrEqual: 'Result starts at ',
          } satisfies Record<KpiComparison, string>,
          suffix: {
            equal: ' and is expected to reach ',
            greaterThan: ' and is expected to exceed ',
            greaterThanOrEqual: ' and is expected to reach or exceed ',
            lessThan: ' and is expected to be less than ',
            lessThanOrEqual: ' and is expected to be less than or equal to ',
          } satisfies Record<KpiComparison, string>,
        },
        startValueOnly: {
          prefix: {
            equal: 'Result starts at ',
            greaterThan: 'Result starts at ',
            greaterThanOrEqual: 'Result starts at ',
            lessThan: 'Result starts at ',
            lessThanOrEqual: 'Result starts at ',
          } satisfies Record<KpiComparison, string>,
          suffix: {
            equal: '',
            greaterThan: ' and is expected to increase',
            greaterThanOrEqual: ' and is expected to increase',
            lessThan: ' and is expected to decrease',
            lessThanOrEqual: ' and is expected to decrease',
          } satisfies Record<KpiComparison, string>,
        },
      },
      expectedResult: 'Expected Result',
      confidenceSlider: {
        balanced: 'Balanced',
        certain: 'Certain',
        notConfident: 'Not confident',
        quiteConfident: 'Quite Confident',
        somewhatUncertain: 'Somewhat uncertain',
      },
      fulfillmentStates: {
        failed: 'The task has been unsuccessful in achieving its objectives.',
        completed:
          'The task is complete, and all __targets__ have been reached.',
        unknown: 'The task is in progress, and the __target__ is uncertain.',
        failedTitle: 'Failed',
        completedTitle: 'Completed',
        unknownTitle: 'In Progress',
      },
      confidenceStep: 'How confident are you in fully delivering on time?',
      confidenceStepContinuousAction:
        'How confident are you in delivering the committed __Action__ Achievement?',
      createFirstCheckin: 'Create First Check-In',
      due: 'Due',
      emptyCheckinList:
        'You have no items assigned to you that require a check-in.',
      headline: 'Status Check-In',
      latestCheckin: 'Latest Check-In',
      latestVersion: 'Latest Version',
      newCheckin: 'New Check-In',
      noCheckinsHistorical: 'No Check-Ins',
      progressStatusStep: 'What is the current status of this item?',
      resultStep: "What's your progress on the __actions__?",
      percentualProgressStep:
        'How much of the needed work has already been completed?',
      subheader: 'Check-in on your __targets__ and __actions__',
      emptyState: {
        headline: 'No Check-in created yet.',
        descriptionUnassigned:
          "This __action__ hasn't been assigned to anyone yet.",
        descriptionCurrentUser: 'You are responsible for this __action__.',
        descriptionOtherUser:
          '{userName:string} has not created a check-in yet.',
        ctaCheckinNow: 'Create a check-in now',
      },
    },
    common: {
      accountable: 'Accountable',
      accountableTeam: 'Accountable Team',
      actions: '__Actions__',
      activeAoas: 'Active AOAs',
      draftAoas: 'Draft AOAs',
      alreadyImported: 'Already imported',
      aoaTitle: 'AOA Title',
      approver: 'Approver',
      archivedAoas: 'Archived AOAs',
      checkin: 'Status Check-in',
      showMetrics: 'Show Metrics',
      confidence: 'Confidence',
      emptyIntents: 'No __intents__ have been added yet.',
      emptyLooseOutcomes: 'No __targets__ have been added yet.',
      emptySharedReality: 'No shared reality and knowledge has been added yet.',
      parentAoaNotApproved: 'The parent AOA has not been approved yet',
      parentAoaNotApprovedDescription:
        'You can start adding items as soon the parent AOA is approved.',
      higherIntents: '__Higher Intents__',
      inherted: 'Inherited',
      intents: '__Intents__',
      itemHistory: 'Item History',
      iteration: 'Iteration',
      noIteration: 'No iteration',
      outcome: '__Target__',
      outcomes: '__Targets__',
      parentAoa: 'Parent AOA',
      progress: 'Progress',
      responsible: 'Responsible',
      result: 'Result',
      selectedIteration: 'Assigned Iteration',
      strategicContext: 'Shared Reality and Knowledge',
      text: 'Text',
      title: 'Title',
      untitledAoa: 'Untitled AOA',
      actionAchievment: '__Action__ Achievement',
      latestCheckinComment: 'Latest Check-In Comment',
      isContinuousActionDescription:
        'Untick this box to remove progress tracking for this __Action__ — when effort is unclear at planning time or tracking adds no value for recurring __Actions__.',
      isContinuousAction: 'Progress Tracking',
    },
    details: {
      pageHeader: 'AOA Details',
      pageSubheader: 'View and manage an AOA',
    },
    filterPage: {
      cardHeader: 'All __Intents__, __Targets__, __Actions__ & Results',
      lastCheckinRangeLabel: 'Last Check-in',
      noResultsDescription: 'Your applied filters returned no results.',
      pageHeader: 'AOA Dashboard',
      pageSubheader: 'Filter and view AOAs and their items',
    },
    timePeriod: {
      label: 'Time Period',
      unknown: 'Unknown',
      onlyStart: 'Since {periodStart:string}',
      onlyEnd: 'Until {periodEnd:string}',
      range: 'From {periodStart:string} to {periodEnd:string}',
      singleDay: 'On {periodStart:string}',
    },
    forms: {
      requiredField: 'Required',
      accountableRequired: 'Accountable person is required',
      accountableTeamRequired: 'Accountable team is required',
      resultRequired: 'Result is required',
      add: {
        action: 'Add __action__…',
        higherIntent: 'Add higher __intent__…',
        intent: 'Add __intent__…',
        outcome: 'Add __target__…',
        text: 'Add item…',
        intentOrAction: 'Add __intent__/__action__…',
      } satisfies Record<AoaItemType, string> & Record<string, string>,
      booleanTypeLabel: 'Fulfilled or not fulfilled',
      confidenceRequired: 'Confidence is required',
      iterationRequired: 'Iteration is required',
      numericTypeLabel: 'Measurable Progress',
      periodEnd: 'End Date',
      periodEndRequired: 'End date is required',
      periodStart: 'Start Date',
      periodStartRequired: 'Start date is required',
      plannedQuantifier: 'Planned Quantifier',
      plannedStartingQuantifier: 'Starting Planned Quantifier',
      responsibleRequired: 'Responsible person is required',
      selectResponsible: 'Select responsible…',
      textRequired: 'Text is required',
      titleRequired: 'Title is required',
      removeResult: 'Remove Result',
      saveResult: 'Save Result',
      addResult: 'Add __Deliverables__',
      editResult: 'Edit __Deliverable__',
      addKpi: 'Add Key Performance Indicator',
      removeKpi: 'Remove Key Performance Indicator',
      kpi: 'Key Performance Indicator',
      comperator: 'Comparison',
      description: 'Description',
      addDescription: 'Add Description',
      referenceLink: 'Reference Link',
      addReferenceLink: 'Add Reference Link',
      parentItem: 'Parent Item',
    },
    import: {
      exitImport: 'Exit Import',
      importButtonLabel: 'Import {0:number} item{{s}}',
      importButtonLabelEmpty: 'Select items to import',
      importCardHeadline: 'Import from Previous Iteration	',
      importCardSubheadline:
        'Items can either moved or duplicated from the previous iteration. Moved items are linked to the previous iteration and will show changes made in the previous iteration. Duplicated items are independent of the previous iteration.',
      importFromPreviousIteration: 'Import from previous iteration',
      importPreviousAssignedHeadline: 'Previously assigned items',
      showAllAvailableItems: 'Show all {status:string} items',
      hideAllAvailableItems: 'Hide all {status:string} items',
      showAllItems: 'Show all items',
      hideAllItems: 'Hide all items',
      noPreviousAssignedItemsAvailable:
        'There are no items available to import from the previous iteration.',
      importFromPreviousIterationAction:
        'Import __Action__ from previous iteration',
      importFromPreviousIterationIntent:
        'Import __Intent__ from previous iteration',
      importFromPreviousIterationIntentAction:
        'Import __Intent__/__Action__ from previous iteration',
      importFromPreviousIterationLooseOutcome:
        'Import __Targets__ from previous iteration',
      importFromPreviousIterationRootIntent:
        'Import Higher __Intent__ from previous iteration',
      importItemsFromLabel: 'Import items from…',
      importModeSelector: 'How should the items be imported?',
      importModeSelectorDescription:
        'Select whether to duplicate or move the items.',
      importModeSelectorDuplicate: 'Duplicate',
      importModeSelectorMove: 'Move',
      importModeSelectorDuplicateDescription:
        '__Intents__ or __Actions__ will be copied without reference to the previous __Intent__ or __Action__. Previous check-ins will not be linked.',
      importModeSelectorMoveDescription:
        '__Intents__ or __Actions__ will be moved including reference and check-ins to the previous __Intent__ or __Action__.',
      importModeSelectorDuplicateDescriptionTargets:
        '__Targets__ will be copied without reference to the previous __Target__. Previous check-ins will not be linked.',
      importModeSelectorMoveDescriptionTargets:
        '__Targets__ will be moved including reference and check-ins to the previous __Target__.',
      importModeSelectorMoveSelected: 'Moving items from previous iteration',
      importModeSelectorDuplicateSelected:
        'Duplicating items from previous iteration',
      importNoItemsAvailable: 'Nothing to import',
      importNoItemsAvailableParentAoaDescription:
        'It seems that there are no items available to import from the parent AOA.',
      importNoItemsAvailablePreviousIterationDescription:
        'It seems that there are no items available to import from the previous iteration.',
      importSectionCardHeadline: 'Import „{section:string}“',
      importSuccessMessage: 'Successfully imported {0:number} item{{s}}',
      movedToNextIterationDescription: 'Moved to next iteration',
      movedToNextIterationShort: 'moved',
    },
    infos: {
      confirmApproveAoa: 'This will take the AOA out of the draft state.',
      confirmApproveAoaDescription:
        'Once approved, no items can be added or deleted.',
      withdrawAoa: 'This will close {aoaName:string}.',
      withdrawAoaDescription:
        'Once closed, users can no longer create check-ins.',
      confirmDeleteAoa: "This will delete the AOA and all it's items.",
      confirmMultipaste: 'This will add multiple items at once.',
      confirmTreeDelete:
        'This may result in the deletion of items below this one, which may have an impact on other AOAs.',
      dragToReorder: 'Drag to reorder',
      enterOrDetails: 'Press enter to add item, or provide more details.',
      enterOrPaste: 'Press enter to add item, or paste multiple items at once.',
    },
    iterationUnavailableReasons: {
      'aoa-cycle-contained-by-existing':
        'The AOA already has a cycle that fully contains the iteration.',
      'aoa-cycle-exists': 'The AOA already has a cycle for the period.',
      'parent-aoa-not-approved': 'The parent AOA is not approved.',
    } satisfies Record<IterationUnavailableReason, string>,
    menu: {
      addAction: 'Add __Action__',
      addChildAoa: 'Create New Sub AOA',
      addIteration: 'Create New Iteration',
      checkin: 'Create Check-in',
      withdraw: 'Close AOA',
      delete: 'Delete',
      deleteAoa: 'Delete AOA',
      edit: 'Edit Details',
      renameAoa: 'Rename AOA',
      history: 'Show History',
      viewParentAoa: 'View Parent AOA',
      editContent: 'Edit Content',
      dangerZone: 'Danger Zone',
      newAoa: 'Create new AOAs',
    },
    deleteItem: {
      deleteModalTitle: 'Are you sure you want to delete this item?',
    },
    newAoa: {
      createNewIteration: 'Create New Iteration',
      emptyStates: {
        create: {
          company: 'Create a company AOA to get started.',
          department: 'Create your first Department AOA',
          team: 'Create your first Team AOA',
          general: 'Create your first AOA',
        },
        emptyContextInheritanceNotice:
          "If no items are added, the items of the parent AOA's iteration will be inherited.",
        noPermissionsToCreate: {
          company: 'No Company AOA was created yet',
          department: 'No Department AOA was created yet',
          team: 'No Team AOA was created yet',
          general: 'No AOA was created yet',
        },
        setupOrganization: 'Setup Organization',
        unavailable: {
          company: 'Setup your organization first',
          department: 'Create a company AOA first',
          team: 'Create a department AOA first',
          general: 'Setup your organization first',
        },
      },
      failedToCreateAoa: 'Failed to create AOA',
      iterationSourceLabel: 'Pick an AOA to create a new iteration',
      metadataPanelHeader: 'Title, Iteration & Team',
      newAoaDescription:
        'Create a new blank AOA. Pick a parent AOA and a period to get started.',
      newAoaLabel: 'New AOA',
      newIterationDescription:
        'Add a new iteration to an existing AOA. This will create a new version of the AOA for a new period.',
      newIterationLabel: 'New Iteration',
      pageSubheader:
        'Create a new AOA or add a new iteration to an existing one',
      parentAoa: 'Parent AOA of {0:string}',
      parentSourceLabel: 'Select the Parent AOA',
      pickAoaLabel: 'Pick an AOA',
      pickParentAoaLabel: 'Pick a parent AOA',
      previousIterationCycle: 'Previous Iteration',
      selectAoaPlaceholder: 'Select AOA…',
      selectAoaType: 'Select AOA Type',
      selectParentAoaIteration: 'Select Parent AOA Iteration',
    },
    overview: {
      emptyAoaList: "Your organization doesn't have any AOAs yet.",
      pageHeader: 'AOA Overview',
      pageSubheader: "View and manage the organization's AOAs",
    },
    progress: {
      blocked: 'Blocked',
      cancelled: 'Cancelled',
      delayed: 'Delayed',
      done: 'Finished',
      'on track': 'On track',
      open: 'open',
      unknown: 'Unknown',
    } satisfies Record<AoaProgressStatus | 'open' | 'unknown', string>,
    yesNoOutcome: {
      completed: 'Completed',
      failed: 'Failed',
    },
    progressDescription: {
      blocked: 'External factors are preventing you from making progress.',
      cancelled:
        'The __target__ is no longer relevant. You will no longer receive reminders',
      delayed: 'You’re making progress, but you’re behind schedule.',
      done: 'All planned activities have been conducted. You will no longer receive reminders.',
      'on track': 'You are making progress as planned.',
    } satisfies Record<AoaProgressStatus, string>,
    sectionHelpers: {
      beliefs:
        'What are our key assumptions and working hypotheses for success?',
      boundaries:
        'What must not happen during implementation? What do we explicitly not want to do?',
      challenges: 'What essential challenges are we facing?',
      higherIntents:
        'What are the higher __intents__ that guide your strategy?',
      input: 'What key resources are available to us for implementation?',
      intentsActions:
        'What do you really, really, really want to accomplish from now until the conclusion of the period?',
      looseOutcomes:
        'What are the __targets__ and __actions__ you want to achieve?',
      mission: 'What does the ideal future state of success look like?',
      statusQuo: 'Where are we today? Describe the current situation.',
      vision: 'Why do we go to work? What is our overarching purpose?',
    } satisfies Record<AoaSection, string> & Record<string, string>,
    sections: {
      beliefs: 'Beliefs',
      boundaries: 'Boundaries',
      challenges: 'Challenges',
      higherIntents: '__Higher Intents__',
      input: 'Resources',
      intentsActions: 'Strategy Alignment & Commitment',
      looseOutcomes: '__Targets__',
      mission: 'Mission',
      statusQuo: 'Status Quo',
      vision: 'Vision',
    } satisfies Record<AoaSection, string> & Record<string, string>,
    tabSections: {
      intentsActions: '__Intents__ & __Actions__',
      outcomesResults: '__Targets__',
      sharedReality: 'Shared Reality',
      overview: 'Overview',
      strategy: 'Strategy',
      teamwork: 'Teamwork',
      visionMissionHigherIntents: 'Vision, Mission & __Higher Intents__',
    },
    tooltips: {
      action:
        'Describes the detailed steps that needs to be done in order to achieve the related __Intent__. Every __Action__ shall describe concrete jobs and provide at least one qualitative __target__ or quantitative __deliverable__.',
      higherIntent:
        'The most important and specific achievement that you the parent department want to reach in the next 12 months starting now.',
      intent:
        'Describes the most important and specific achievement that you want to reach in the next 12 months starting now. It defines only what you can directly influence and contribute to realize the dedicated Higher Intent. It is the specific guidance that doesn’t have to be measurable. (Think in right directions not tasks or results.)',
      movedFromPreviousIterationCount:
        'Moved {0:number} time{{s}} from previous iteration{{s}}',
      outcome:
        'Each Action requires at least one qualitative outcome or one quantitative result. For outcome definition think in "Definition of Done". For results, think in the ambition level and the right metric. Results and outcomes can be bound to an Action or an AOA.',
      rootIntent:
        'Describes what you want to achieve, through defining the most important strategic pillars of the company’s mission for the next 3 years. It provides guidance for focused strategic execution.',
    },
    tree: {
      confidenceColHeader: 'Confidence',
      lastCheckinColHeader: 'Last Check-In',
      progressColHeader: 'Progress/Status',
    },
    detailSheet: {
      tabs: {
        checkinTab: 'Checkins',
        targetsTab: 'Targets',
        activityTab: 'Activity',
        treeTab: 'Strategy Tree',
      },
    },
    surveys: {
      createNewSurvey: 'Create a new survey',
      createAccessLink: 'Create survey link',
      createAccessLinkButton: 'Create link',
      createAccessLinkDescription:
        'Create a link for anonymous access to the survey. This link can be shared with anyone.',
      newSurvey: 'New survey',
      editSurvey: 'Edit survey',
      createSurveyLink: 'Create survey link',
      surveyLink: 'Survey link',
      surveyTitle: 'Survey title',
      collectTeamId: "Collect User's Team",
      collectTeamIdDescription:
        'Whether the user must select his team before answering the survey.',
      collectUsername: "Collect User's Name",
      collectUsernameDescription:
        'Whether the user must select his name before answering the survey.',
      maxUses: 'Max uses',
      accessLinkExpiration: 'Expiration date',
      tabSections: {
        questions: 'Survey Content',
        responses: 'Responses',
        results: 'Results',
        access: 'Access & Sharing',
        reality: 'Shared Reality',
      },
      questions: {
        video: 'Video',
      },
      noResponses: 'No responses available',
      noResponsesDescription: 'There are no responses to this survey yet.',
      reformulatedResponse: 'Reformulated Response',
      reformulateResponse: 'Reformulate Response',
      responseCount: '{count:number} response{{s}}',
    },
  },
} as const satisfies BaseTranslation

export default en_aoa
